import mergeObjects from '@bybeam/platform-lib/answers/mergeObjects';
import {
  ApplicationConfig,
  ApplicationSection,
  CommonQuestionKeys,
  KCCustomQuestionKeys,
  SMBCapitalCustomQuestionKeys,
} from '@bybeam/platform-types';
import STANDARD_COMPONENTS from '../standards/index.js';
import { SpecialSectionKeys } from '../standards/types.js';
import { QUESTIONS } from './questions/capitalQuestions.js';
import { QUESTIONS as COMMON_QUESTIONS } from './questions/sharedQuestions.js';

const SECTIONS: ApplicationSection[] = [
  {
    key: 'personalInfo',
    name: 'About the Entrepreneur/Business Owner',
    overview: {
      title: 'About the Entrepreneur/Business Owner',
      description: `The following questions are about the owner of the business, including personal net worth, social determinants, and household information.

This information is critical to understanding the status of the business during the pandemic and what hurdles you may be facing as a business owner.`,
    },
    questionGroups: [
      {
        key: 'personalInfo',
        name: 'About the Entrepreneur/Business Owner',
        questions: [
          QUESTIONS[CommonQuestionKeys.GENDER],
          QUESTIONS[SMBCapitalCustomQuestionKeys.BIRTH_YEAR],
          QUESTIONS[SMBCapitalCustomQuestionKeys.BORN_IN_US],
          QUESTIONS[CommonQuestionKeys.ETHNICITY],
          QUESTIONS[CommonQuestionKeys.LANGUAGE],
          QUESTIONS[CommonQuestionKeys.HOUSING_TYPE],
          QUESTIONS[CommonQuestionKeys.ADDRESS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.BUSINESS_HOURS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.OTHER_EMPLOYMENT],
          QUESTIONS[SMBCapitalCustomQuestionKeys.ACADEMIC_STATUS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.SECTOR],
          QUESTIONS[SMBCapitalCustomQuestionKeys.EDUCATION_LEVEL],
          QUESTIONS[SMBCapitalCustomQuestionKeys.STUDENT_DEBT],
          QUESTIONS[SMBCapitalCustomQuestionKeys.PANDEMIC_DEBT_PAYMENTS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.CRIMINAL_HISTORY_OR_FELONY],
          QUESTIONS[CommonQuestionKeys.VETERAN],
          QUESTIONS[CommonQuestionKeys.DISABLED],
          QUESTIONS[CommonQuestionKeys.MARITAL_STATUS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.DEPENDENTS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.ISSUES_WITH_AFFORDABLE_CHILDCARE],
          QUESTIONS[SMBCapitalCustomQuestionKeys.TRANSPORTATION],
          QUESTIONS[CommonQuestionKeys.HOUSEHOLD_INCOME],
          QUESTIONS[SMBCapitalCustomQuestionKeys.HAS_BANK_ACCOUNT],
          QUESTIONS[SMBCapitalCustomQuestionKeys.OWNS_ASSETS],
          COMMON_QUESTIONS[KCCustomQuestionKeys.CREDIT_SCORE],
          QUESTIONS[SMBCapitalCustomQuestionKeys.CREDIT_SCORE_ASSISTANCE],
          QUESTIONS[SMBCapitalCustomQuestionKeys.BUSINESS_REASON],
        ],
      },
    ],
  },
  {
    key: 'businessInfo',
    name: 'About the Small Business',
    overview: {
      description: `The following questions are about the business, including the types of products and services offered, business classification, and revenue/sales. 

This information is critical to understanding the business traction and resources that are needed to support your business.`,
      title: 'About the Small Business',
    },
    questionGroups: [
      {
        key: 'businessInfo',
        name: 'About the Small Business',
        questions: [
          QUESTIONS[SMBCapitalCustomQuestionKeys.NAME_OF_BUSINESS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.DOING_BUSINESS_AS],
          COMMON_QUESTIONS[KCCustomQuestionKeys.YEAR_ESTABLISHED],
          QUESTIONS[SMBCapitalCustomQuestionKeys.LEGAL_STRUCTURE],
          QUESTIONS[SMBCapitalCustomQuestionKeys.OPERATING_AGREEMENT],
          QUESTIONS[SMBCapitalCustomQuestionKeys.INSURED],
          QUESTIONS[SMBCapitalCustomQuestionKeys.INDUSTRY],
          QUESTIONS[SMBCapitalCustomQuestionKeys.SOCIAL_IMPACT_VENTURE],
          QUESTIONS[SMBCapitalCustomQuestionKeys.BRANDING_MATERIALS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.BRANDING_MATERIALS_INTERESTED_IN],
          QUESTIONS[SMBCapitalCustomQuestionKeys.SOCIAL_MEDIA],
          QUESTIONS[SMBCapitalCustomQuestionKeys.RESOURCES_NEEDED],
          QUESTIONS[SMBCapitalCustomQuestionKeys.REVENUE_FROM_ONLINE_SALES],
          QUESTIONS[SMBCapitalCustomQuestionKeys.ONLINE_AD_BUDGET],
          QUESTIONS[SMBCapitalCustomQuestionKeys.REVENUE_FROM_POP_UPS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.AVERAGE_ANNUAL_REVENUE_RANGE],
          QUESTIONS[SMBCapitalCustomQuestionKeys.OPERATING_IN_KCMO],
          QUESTIONS[SMBCapitalCustomQuestionKeys.INTERACTED_WITH_CITY_DEPARTMENTS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.EXPERIENCE_OBTAINING_A_BUSINESS_LICENSE],
        ],
      },
    ],
  },
  {
    key: 'capitalReadiness',
    name: 'About Capital Readiness and Accessing Capital',
    overview: {
      title: 'About Capital Readiness and Accessing Capital',
      description: `The following questions are about the business' capital readiness and access to capital, including current financing, planned financing, and banking information.

This information is critical to understanding the challenges and opportunities the business faces in accessing capital.`,
    },
    questionGroups: [
      {
        key: 'capitalReadiness',
        name: 'About Capital Readiness and Accessing Capital',
        questions: [
          QUESTIONS[SMBCapitalCustomQuestionKeys.HAS_BUSINESS_ACCOUNT],
          QUESTIONS[SMBCapitalCustomQuestionKeys.HAS_RELATIONSHIP_WITH_A_BANKER],
          QUESTIONS[SMBCapitalCustomQuestionKeys.USES_AN_ACCOUNTANT],
          QUESTIONS[SMBCapitalCustomQuestionKeys.HAS_BUSINESS_CREDIT_CARD],
          QUESTIONS[SMBCapitalCustomQuestionKeys.HAS_APPLIED_FOR_BUSINESS_LOANS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.CHALLENGES_FACED_ACCESSING_CAPITAL],
          QUESTIONS[SMBCapitalCustomQuestionKeys.COVID_RELIEF_PROGRAMS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.RAISING_CAPITAL],
          QUESTIONS[SMBCapitalCustomQuestionKeys.POS_SYSTEM],
        ],
      },
    ],
  },
  {
    key: 'location',
    name: 'About the Business Location/Office Space',
    overview: {
      title: 'About the Business Location/Office Space',
      description: `The following questions are about the business' location, physical space, and plans for growth.  

This information is critical to understanding the business' physical needs and goals.`,
    },
    questionGroups: [
      {
        key: 'location',
        name: 'About the Business Location/Office Space',
        questions: [
          QUESTIONS[SMBCapitalCustomQuestionKeys.BUSINESS_ADDRESS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.LOCATION_CATEGORY],
          QUESTIONS[SMBCapitalCustomQuestionKeys.SQUARE_FOOTAGE],
          QUESTIONS[SMBCapitalCustomQuestionKeys.STARTED_AT_HOME],
          QUESTIONS[SMBCapitalCustomQuestionKeys.PLAN_TO_MOVE_TO_LARGER_SPACE],
        ],
      },
    ],
  },
  {
    key: 'workforce',
    name: 'About the Business Workforce',
    overview: {
      title: 'About the Business Workforce',
      description: `The following questions are about your workforce, including number of full-time and part-time employees, salaries, and planned future hires. 

This information is critical to understanding workforce development opportunities.`,
    },
    questionGroups: [
      {
        key: 'workforce',
        name: 'About the Business Workforce',
        questions: [
          QUESTIONS[SMBCapitalCustomQuestionKeys.PAY_YOURSELF_AS_W2],
          QUESTIONS[SMBCapitalCustomQuestionKeys.HAS_EMPLOYEES],
          QUESTIONS[SMBCapitalCustomQuestionKeys.NUMBER_OF_FULLTIME_EMPLOYEES],
          QUESTIONS[SMBCapitalCustomQuestionKeys.OFFERS_BENEFITS_FULLTIME],
          QUESTIONS[SMBCapitalCustomQuestionKeys.FULL_TIME_BENEFITS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.NUMBER_OF_PARTTIME_EMPLOYEES],
          QUESTIONS[SMBCapitalCustomQuestionKeys.OFFERS_BENEFITS_PARTTIME],
          QUESTIONS[SMBCapitalCustomQuestionKeys.PART_TIME_BENEFITS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.PLAN_TO_HIRE],
          QUESTIONS[SMBCapitalCustomQuestionKeys.PLANNED_FULLTIME_ROLES],
          QUESTIONS[SMBCapitalCustomQuestionKeys.PLANNED_PARTTIME_ROLES],
        ],
      },
    ],
  },
  {
    key: 'supplierDiversity',
    name: 'About Supplier Diversity',
    overview: {
      title: 'About Supplier Diversity',
      description: `The following questions are about supplier diversity, including certifications and corporate contracts.

This information is critical to understanding whether organizations are sourcing goods and services from underestimated groups, including women, minorities, etc.`,
    },
    questionGroups: [
      {
        key: 'supplierDiversity',
        name: 'About Supplier Diversity',
        questions: [
          QUESTIONS[SMBCapitalCustomQuestionKeys.SELL_TO_GOV_OR_CORP],
          QUESTIONS[SMBCapitalCustomQuestionKeys.CERTIFICATIONS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.HAS_GOV_CONTRACT],
          QUESTIONS[SMBCapitalCustomQuestionKeys.PART_OF_CORP_DIVERSITY_PROGRAM],
          QUESTIONS[SMBCapitalCustomQuestionKeys.HAS_CORP_CONTRACT],
        ],
      },
    ],
  },
  {
    key: 'technology',
    name: 'About Technology',
    overview: {
      title: 'About Technology',
      description: `The following questions are about the technology the business uses.

This information is critical to understanding technology needs.`,
    },
    questionGroups: [
      {
        key: 'technology',
        name: 'About Technology',
        questions: [
          QUESTIONS[SMBCapitalCustomQuestionKeys.RUNS_BUSINESS_FROM_HOME],
          QUESTIONS[SMBCapitalCustomQuestionKeys.HAS_WEBSITE],
          QUESTIONS[SMBCapitalCustomQuestionKeys.HAS_HOME_INTERNET],
          QUESTIONS[SMBCapitalCustomQuestionKeys.APPLIED_TO_ACP],
          QUESTIONS[SMBCapitalCustomQuestionKeys.HAS_COMPUTER],
          QUESTIONS[SMBCapitalCustomQuestionKeys.DEVICES],
          QUESTIONS[SMBCapitalCustomQuestionKeys.TECH_INTERESTS],
          QUESTIONS[SMBCapitalCustomQuestionKeys.PUBLIC_ASSISTANCE],
        ],
      },
    ],
  },
  mergeObjects(
    [
      STANDARD_COMPONENTS.definitions.sections[SpecialSectionKeys.Review],
      {
        questionGroups: [
          // Drop the first question and replace it with this one
          { questions: [null, QUESTIONS[SMBCapitalCustomQuestionKeys.FEEDBACK]] },
          // Drop the first question and replace it with this one
          {
            questions: [
              null,
              QUESTIONS[SMBCapitalCustomQuestionKeys.SOURCE],
              QUESTIONS.affirmation,
            ],
          },
        ],
      } as ApplicationSection,
    ],
    'merge',
  ),
];

const CONFIG: ApplicationConfig = {
  introPages: [
    {
      key: 'what-is-it',
      layout: 'default',
      title: 'KC Small Business Capital Program',
      body: `The City of Kansas City, MO in collaboration with the Economic Development Corporation of Kansas City are working together to assist eligible small businesses that experienced negative economic impacts or disproportionate impacts from the COVID-19 pandemic. The goal of the capital program is to create a small business ecosystem for capital readiness and accessible starting capital.

Kansas City, Missouri entrepreneurs and small business owners should complete this questionnaire to see if their business is eligible for business development support, capital planning tools, connection to subject matter experts, and/or capital and funding.
      `,
    },
    STANDARD_COMPONENTS.definitions.introPages.ApplicationProcess,
  ],
  overrides: {
    statuses: [
      {
        status: 'Application',
        label: 'Capital and technical assistance for small businesses',
        message:
          'Your business may be eligible for one of various forms of business development assistance, including educational and/or financial. Apply now!',
      },
      {
        status: 'InProgress',
        label: 'Continue where you left off',
        message:
          'You’ve made great progress so far! Keep working on your application to receive business development assistance.',
      },
      {
        status: 'AwaitingReview',
        label: 'Status: Awaiting Review',
        message:
          'Thank you for submitting your application! You will be notified if our team needs additional information and/or of any next steps. Check back here for updates.',
      },
      {
        status: 'Approved',
        message:
          'Your business was found eligible for one of our KC SMB Capital Programs. Check your email for a referral to a program-specific application or look at the Beam landing page for newly available applications. A referral does not guarantee you will receive funding.',
      },
    ],
    submission: {
      title: 'What happens next?',
      bullets: [
        {
          key: 'review',
          title: 'Application review',
          icon: 'description',
          description:
            'While we look at your application, we might send you an email to ask for more information or notify you of next steps',
        },
      ],
    },
  },
  sections: SECTIONS,
};

export default CONFIG;
